'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import { Button } from '@components/ui/button';
import { useFormItemVariant } from '@components/ui/form-item-variant-context';
import { VerificationShieldIndicator } from '@lib/security/components/verification-shield-indicator';
import { cn } from 'cn';
import type { CountryCode } from 'libphonenumber-js';
import { ChevronDown, Phone } from 'lucide-react';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { COUNTRY_OPTIONS } from '../utils/country-options';
import { DEFAULT_PHONE_COUNTRY, explodeE164, formatPartial, parsePhoneToE164 } from '../utils/phone';
const COUNTRY_BY_CODE = new Map(COUNTRY_OPTIONS.map(option => [option.code, option]));
interface PhoneInputProps {
  /** Canonical E.164 (or the raw national string when not yet parseable, so a
   *  schema can still reject it). */
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  showVerified?: boolean;
  // Injected by shadcn `FormControl` to wire labels, messages, and error
  // styling — mirrors how the `Input` primitive consumes them.
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
  ref?: React.Ref<HTMLInputElement>;
}

/**
 * Theme-aware phone editor: a country picker (flag + dial code) plus a
 * national-format input, bound to a canonical E.164 value. The semantic-token
 * counterpart to the contact-drawer and everything-bar phone rows, which are
 * pinned to those surfaces' dark palette.
 */
export function PhoneInput(t0) {
  const $ = _c(61);
  const {
    value,
    onChange,
    onBlur,
    disabled,
    id,
    placeholder,
    showVerified: t1,
    "aria-invalid": ariaInvalid,
    "aria-describedby": ariaDescribedby,
    ref
  } = t0;
  const showVerified = t1 === undefined ? false : t1;
  const invalid = ariaInvalid === true || ariaInvalid === "true";
  let t2;
  if ($[0] !== value) {
    t2 = explodeE164(value);
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const exploded = t2;
  const [national, setNational] = useState(exploded?.national ?? value);
  const [country, setCountry] = useState(exploded?.country ?? DEFAULT_PHONE_COUNTRY);
  const [pickerOpen, setPickerOpen] = useState(false);
  const isField = useFormItemVariant() === "field";
  const internalRef = useRef(null);
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    const next = explodeE164(value);
    setNational(next?.national ?? value);
    setCountry(next?.country ?? DEFAULT_PHONE_COUNTRY);
  }
  let t3;
  if ($[2] !== country) {
    t3 = COUNTRY_BY_CODE.get(country);
    $[2] = country;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const current = t3;
  const setRefs = function setRefs(node) {
    internalRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else {
      if (ref) {
        ref.current = node;
      }
    }
  };
  let t4;
  if ($[4] !== onChange) {
    t4 = function commitPhoneValue(displayNational, countryCode) {
      const committed = parsePhoneToE164(displayNational, countryCode) ?? displayNational;
      setLastValue(committed);
      onChange(committed);
    };
    $[4] = onChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const commitPhoneValue = t4;
  let t5;
  if ($[6] !== commitPhoneValue || $[7] !== country) {
    t5 = function handleNationalChange(rawInput) {
      const displayNational_0 = formatPartial(rawInput, country);
      setNational(displayNational_0);
      commitPhoneValue(displayNational_0, country);
    };
    $[6] = commitPhoneValue;
    $[7] = country;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleNationalChange = t5;
  const handleSelectCountry = function handleSelectCountry(countryCode_0) {
    setCountry(countryCode_0);
    setPickerOpen(false);
    const displayNational_1 = national ? formatPartial(national, countryCode_0) : national;
    setNational(displayNational_1);
    commitPhoneValue(displayNational_1, countryCode_0);
    setTimeout(() => internalRef.current?.focus(), 0);
  };
  const t6 = isField && "h-11";
  let t7;
  if ($[9] !== disabled || $[10] !== invalid || $[11] !== t6) {
    t7 = cn("border-input bg-background flex h-9 min-w-0 items-center gap-2 rounded-sm border px-3 shadow-sm transition-[color,box-shadow,border]", "focus-within:border-ring focus-within:ring-ring focus-within:ring", "refresh:hover:border-ring refresh:rounded-lg refresh:shadow-none refresh:duration-150 refresh:ease-out", "refresh:focus-within:border-primary refresh:focus-within:ring-primary refresh:focus-within:ring-1", t6, {
      "border-destructive focus-within:border-destructive focus-within:ring-destructive": invalid,
      "pointer-events-none opacity-50": disabled
    });
    $[9] = disabled;
    $[10] = invalid;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = "PhoneInput";
  const t9 = "phone-input.tsx";
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Phone className="text-muted-foreground h-4 w-4 shrink-0" data-sentry-element="Phone" data-sentry-source-file="phone-input.tsx" />;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const T0 = Popover;
  const t11 = "Popover";
  const t12 = "phone-input.tsx";
  const t13 = current?.flag ?? "\uD83C\uDFF3\uFE0F";
  let t14;
  if ($[14] !== t13) {
    t14 = <span className="leading-none">{t13}</span>;
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const t15 = current?.dial ?? "+1";
  let t16;
  if ($[16] !== t15) {
    t16 = <span className="font-medium">{t15}</span>;
    $[16] = t15;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ChevronDown className="h-3 w-3 opacity-50" data-sentry-element="ChevronDown" data-sentry-source-file="phone-input.tsx" />;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== disabled || $[20] !== t14 || $[21] !== t16) {
    t18 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="phone-input.tsx"><Button type="button" variant="tertiary" size="sm" disabled={disabled} aria-label="Select country" className="text-muted-foreground hover:text-foreground h-auto shrink-0 gap-1 rounded-sm p-0 hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0" data-sentry-element="Button" data-sentry-source-file="phone-input.tsx">{t14}{t16}{t17}</Button></PopoverTrigger>;
    $[19] = disabled;
    $[20] = t14;
    $[21] = t16;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const T1 = PopoverContent;
  const t19 = "w-[260px] p-0";
  const t20 = "start";
  const t21 = "PopoverContent";
  const t22 = "phone-input.tsx";
  const T2 = Command;
  const t23 = "Command";
  const t24 = "phone-input.tsx";
  let t25;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <CommandInput placeholder={"Search country\u2026"} className="h-9" data-sentry-element="CommandInput" data-sentry-source-file="phone-input.tsx" />;
    $[23] = t25;
  } else {
    t25 = $[23];
  }
  const T3 = CommandList;
  const t26 = "max-h-64";
  const t27 = "CommandList";
  const t28 = "phone-input.tsx";
  let t29;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <CommandEmpty className="py-3 text-center text-sm" data-sentry-element="CommandEmpty" data-sentry-source-file="phone-input.tsx">No country found.</CommandEmpty>;
    $[24] = t29;
  } else {
    t29 = $[24];
  }
  const t30 = COUNTRY_OPTIONS.map(opt => <CommandItem key={opt.code} value={`${opt.name} ${opt.dial} ${opt.code}`} onSelect={() => handleSelectCountry(opt.code)} className="text-sm"><span className="mr-2 leading-none">{opt.flag}</span><span className="flex-1 truncate">{opt.name}</span><span className="text-muted-foreground ml-2">{opt.dial}</span></CommandItem>);
  let t31;
  if ($[25] !== T3 || $[26] !== t29 || $[27] !== t30) {
    t31 = <T3 className={t26} data-sentry-element={t27} data-sentry-source-file={t28}>{t29}{t30}</T3>;
    $[25] = T3;
    $[26] = t29;
    $[27] = t30;
    $[28] = t31;
  } else {
    t31 = $[28];
  }
  let t32;
  if ($[29] !== T2 || $[30] !== t25 || $[31] !== t31) {
    t32 = <T2 data-sentry-element={t23} data-sentry-source-file={t24}>{t25}{t31}</T2>;
    $[29] = T2;
    $[30] = t25;
    $[31] = t31;
    $[32] = t32;
  } else {
    t32 = $[32];
  }
  let t33;
  if ($[33] !== T1 || $[34] !== t32) {
    t33 = <T1 className={t19} align={t20} data-sentry-element={t21} data-sentry-source-file={t22}>{t32}</T1>;
    $[33] = T1;
    $[34] = t32;
    $[35] = t33;
  } else {
    t33 = $[35];
  }
  let t34;
  if ($[36] !== T0 || $[37] !== pickerOpen || $[38] !== t18 || $[39] !== t33) {
    t34 = <T0 open={pickerOpen} onOpenChange={setPickerOpen} data-sentry-element={t11} data-sentry-source-file={t12}>{t18}{t33}</T0>;
    $[36] = T0;
    $[37] = pickerOpen;
    $[38] = t18;
    $[39] = t33;
    $[40] = t34;
  } else {
    t34 = $[40];
  }
  let t35;
  if ($[41] !== handleNationalChange) {
    t35 = e => handleNationalChange(e.target.value);
    $[41] = handleNationalChange;
    $[42] = t35;
  } else {
    t35 = $[42];
  }
  const t36 = placeholder ?? "(555) 123-4567";
  let t37;
  if ($[43] !== ariaDescribedby || $[44] !== ariaInvalid || $[45] !== disabled || $[46] !== id || $[47] !== national || $[48] !== onBlur || $[49] !== setRefs || $[50] !== t35 || $[51] !== t36) {
    t37 = <input ref={setRefs} id={id} type="tel" inputMode="tel" autoComplete="tel" disabled={disabled} aria-invalid={ariaInvalid} aria-describedby={ariaDescribedby} value={national} onChange={t35} onBlur={onBlur} placeholder={t36} className="text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm font-medium outline-none" />;
    $[43] = ariaDescribedby;
    $[44] = ariaInvalid;
    $[45] = disabled;
    $[46] = id;
    $[47] = national;
    $[48] = onBlur;
    $[49] = setRefs;
    $[50] = t35;
    $[51] = t36;
    $[52] = t37;
  } else {
    t37 = $[52];
  }
  let t38;
  if ($[53] !== showVerified) {
    t38 = showVerified ? <VerificationShieldIndicator label="Phone verified" /> : null;
    $[53] = showVerified;
    $[54] = t38;
  } else {
    t38 = $[54];
  }
  let t39;
  if ($[55] !== t10 || $[56] !== t34 || $[57] !== t37 || $[58] !== t38 || $[59] !== t7) {
    t39 = <div className={t7} data-sentry-component={t8} data-sentry-source-file={t9}>{t10}{t34}{t37}{t38}</div>;
    $[55] = t10;
    $[56] = t34;
    $[57] = t37;
    $[58] = t38;
    $[59] = t7;
    $[60] = t39;
  } else {
    t39 = $[60];
  }
  return t39;
}